import { useEffect, useState } from 'react';
import { ListSidebar } from '../components/ListSidebar';
import { TaskList } from '../components/TaskList';
import { TaskDetail } from '../components/TaskDetail';
import { useLists } from '../hooks/useLists';
import { useTasks } from '../hooks/useTasks';
import { tasksApi, ApiError } from '../api/client';
import { VIEWS, type View } from '../lib/views';
import type { TaskRow } from '../api/types';
import { toast } from '../store/toast';

export function Home() {
  const { lists, error: listsError, create: createList, remove: deleteList } = useLists();
  const [view, setView] = useState<View>(VIEWS.myDay);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [priorityPicker, setPriorityPicker] = useState<{
    task: TaskRow;
    rect: DOMRect;
  } | null>(null);

  // 当前选中的 task
  const { tasks, loading, error, refresh, toggleComplete, update } = useTasks({ view });

  // 加载选中的 task 详情
  const [selectedTask, setSelectedTask] = useState<TaskRow | null>(null);
  useEffect(() => {
    if (!selectedTaskId) {
      setSelectedTask(null);
      return;
    }
    tasksApi
      .get(selectedTaskId)
      .then(setSelectedTask)
      .catch(() => setSelectedTask(null));
  }, [selectedTaskId, tasks]); // tasks 变化时重新拉, 确保拿到最新 version

  // 视图标题
  const viewTitle = (() => {
    switch (view.kind) {
      case 'my-day':
        return '我的一天';
      case 'important':
        return '重要';
      case 'planned':
        return '已计划';
      case 'all':
        return '所有任务';
      case 'completed':
        return '已完成';
      case 'archived':
        return '已归档';
      case 'list': {
        const l = lists.find((x) => x.id === view.listId);
        return l?.name ?? '加载中...';
      }
    }
  })();

  // 切换重要性 (低 0 → 中 1 → 高 2 → 低 0)
  const handleSetImportance = async (task: TaskRow, level: 0 | 1 | 2) => {
    try {
      await update(task.id, { importance: level, version: task.version });
      setPriorityPicker(null);
      toast({
        kind: 'info',
        text:
          level === 0
            ? '已取消优先级'
            : `优先级设为${level === 2 ? '高' : '中'}`,
      });
    } catch (err) {
      toast({ kind: 'error', text: `更新失败: ${(err as Error).message}` });
    }
  };

  return (
    <div className="flex h-full">
      <ListSidebar
        lists={lists}
        currentView={view}
        onSelect={setView}
        onCreateList={async (name) => {
          await createList(name);
        }}
        onDeleteList={deleteList}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* 任务列表 + 详情同屏 (md+) / 单栏 (mobile) */}
      <main className="flex min-w-0 flex-1 overflow-hidden">
        {/* 任务列表: 选中 task 时桌面端缩窄, 列表变宽让详情更紧凑 */}
        <div
          className={`min-w-0 overflow-hidden border-r border-gray-200 dark:border-gray-700 ${
            selectedTaskId ? 'hidden md:flex md:w-1/2 md:min-w-[360px] xl:w-[55%]' : 'flex-1'
          }`}
        >
          <TaskList
            title={viewTitle}
            subtitle={
              view.kind === 'list' ? `${tasks.length} 个任务` : undefined
            }
            tasks={tasks}
            loading={loading}
            error={listsError ?? error}
            selectedTaskId={selectedTaskId}
            onSelectTask={setSelectedTaskId}
            onToggleComplete={(t) => {
              toggleComplete(t).catch((err) => alert(`更新失败: ${err.message}`));
            }}
            onToggleStarred={async (t) => {
              try {
                await update(t.id, { starred: t.starred ? 0 : 1, version: t.version });
              } catch (err) {
                if (err instanceof ApiError) {
                  toast({ kind: 'error', text: `操作失败: ${err.message}` });
                }
              }
            }}
            onLongPress={(t, rect) => setPriorityPicker({ task: t, rect })}
            onRefresh={refresh}
            showQuickAdd={
              view.kind === 'list'
                ? { listId: view.listId, onCreated: refresh }
                : undefined
            }
            onOpenSidebar={() => setSidebarOpen(true)}
            groupByCompletedTime={view.kind === 'completed'}
          />
        </div>

        {/* 详情面板: 桌面端 fixed 宽度卡片, 移动端 fixed 全屏 (TaskDetail 内部处理) */}
        {selectedTask && (
          <div
            className={`shrink-0 overflow-hidden ${
              selectedTaskId ? 'flex' : 'hidden md:flex'
            }`}
          >
            <TaskDetail
              task={selectedTask}
              lists={lists}
              onClose={() => setSelectedTaskId(null)}
              onUpdated={(updated) => {
                if (updated) {
                  setSelectedTask(updated as TaskRow);
                }
                refresh();
              }}
              onDeleted={() => {
                setSelectedTaskId(null);
                refresh();
              }}
            />
          </div>
        )}
      </main>

      {/* 优先级快速选择气泡 (长按触发) */}
      {priorityPicker && (
        <>
          <div
            className="fixed inset-0 z-[60]"
            onClick={() => setPriorityPicker(null)}
          />
          <div
            className="fixed z-[61] w-44 rounded-lg border border-gray-200 bg-white p-1 shadow-xl dark:border-gray-700 dark:bg-gray-800"
            style={{
              top: Math.min(priorityPicker.rect.bottom + 6, window.innerHeight - 130),
              left: Math.min(priorityPicker.rect.left, window.innerWidth - 180),
            }}
          >
            <div className="px-2 py-1 text-xs text-gray-400">设置优先级</div>
            <button
              onClick={() => handleSetImportance(priorityPicker.task, 2)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-red-300" />
              <span className="text-red-700 dark:text-red-300">高</span>
            </button>
            <button
              onClick={() => handleSetImportance(priorityPicker.task, 1)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-orange-300" />
              <span className="text-orange-700 dark:text-orange-300">中</span>
            </button>
            <button
              onClick={() => handleSetImportance(priorityPicker.task, 0)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <span className="w-2.5 h-2.5 rounded-full border border-gray-300" />
              <span className="text-gray-500">无</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
